import { z } from "zod";
//#region src/grant-principal.ts
/**
* The `cp_auth.resource_grants.principal_type` domain — SINGLE SOURCE.
*
* Before R2 this domain was hand-duplicated in 13 files across 4 packages,
* under three different type names. It is declared here once; every consumer
* imports it.
*
* ## Why `'user'` is not a canonical value
* `cp_auth` has NO `users` table. `cloud/cp/cp-auth-api/src/db/schema/operators.ts`
* declares `export const users = cpAuthSchema.table('operators', …)` and later
* aliases `operators = users` — the site-admin slice-6 identity fork renamed the
* Drizzle EXPORT, not the table. SCIM-provisioned people are rows in
* `cp_auth.operators` too (its `source`/`idp_provider`/`external_id` columns).
* So `principal_type='user'` and `='operator'` named the SAME id space, and the
* column carried no information. R2 collapses them and gives the column real
* information by adding `'group'`.
*
* ## Canonical vs accepted
* `GRANT_PRINCIPAL_TYPES` is what may be STORED and what is ever RETURNED.
* `ACCEPTED_GRANT_PRINCIPAL_TYPES` additionally admits the deprecated `'user'`
* on the WIRE, so a separately-installed `zion-ops` binary or a stale SPA
* bundle does not 400 mid-rollout. `normalizeGrantPrincipalType` is the only
* legal way to turn a wire value into a storable one.
*
* ## Why `'machine'` is in BOTH lists (#1644 WS-1)
* ⛔ It is a CANONICAL value, not a wire alias. An external machine identity
* (a GitHub Actions job that federated through `POST /v1/auth/token/federated`)
* is a row in `cp_auth.machine_principals`, referenced by
* `resource_grants.principal_id`, and that row's `principal_type` is STORED as
* `'machine'`. ⇒ It must be in `GRANT_PRINCIPAL_TYPES` or
* `normalizeGrantPrincipalType` answers `null`, `ResourceGrantsService`
* converts that into a 400, and a machine grant is uncreatable through the API
* while the DTO and the Drizzle column enum both look as though they admit it.
* ⛔ Adding it to `ACCEPTED_GRANT_PRINCIPAL_TYPES` alone is therefore the one
* edit that LOOKS complete at every layer and works at none.
*
* ⛔ **It is a THIRD id space, not a synonym for either existing one.**
* `'operator'` and `'user'` name the same id space (see above) and are folded
* together on read; `'group'` and `'machine'` are each exact. A machine's
* `principal_id` is a `cp_auth.machine_principals.id` and can be nothing else —
* see `cp-auth-api/src/modules/permissions/grant-read-predicates.ts`, which
* dispatches on this distinction and must never let a new kind default into the
* human-subject leg.
*/
const GRANT_PRINCIPAL_TYPES = [
	"operator",
	"group",
	"machine"
];
/**
* Deprecated wire alias → canonical value. Never stored, never returned.
*
* ⚠️ Deliberately **null-prototype**. `normalizeGrantPrincipalType` indexes this
* map by an attacker-controlled string; on an ordinary object literal
* `map['toString']` is an inherited **function**, not `undefined`, so a `?? null`
* fallback would hand back a truthy non-`GrantPrincipalType` and every
* downstream `=== 'operator'` comparison would silently be false. With
* `Object.create(null)` every non-own key reads `undefined`. Do not "simplify"
* this back to a bare object literal.
*/
const DEPRECATED_GRANT_PRINCIPAL_TYPE_ALIASES = Object.freeze(Object.assign(Object.create(null), { user: "operator" }));
/**
* Every value a REQUEST may carry: canonical ∪ deprecated aliases.
*
* ⚠️ Hand-written, NOT computed from the two above — a computed union would be
* a `readonly string[]`, and `z.enum()` needs a literal tuple to produce a
* useful type. The set-equality with `GRANT_PRINCIPAL_TYPES ∪
* keys(DEPRECATED_GRANT_PRINCIPAL_TYPE_ALIASES)` is asserted in
* `__tests__/grant-principal.test.ts` instead, so drift is a red rather than a
* type error nobody sees. ⇒ **Adding a canonical value is TWO edits here.**
*/
const ACCEPTED_GRANT_PRINCIPAL_TYPES = [
	"operator",
	"group",
	"machine",
	"user"
];
/**
* Normalize a wire value to a storable one.
*
* Returns `null` for anything outside `ACCEPTED_GRANT_PRINCIPAL_TYPES`. Callers
* MUST convert `null` into a 400, never into a silently-dropped filter — D-10:
* an unparseable principal type is UNRESOLVABLE, not "matches nothing".
*/
function normalizeGrantPrincipalType(raw) {
	if (GRANT_PRINCIPAL_TYPES.includes(raw)) return raw;
	return DEPRECATED_GRANT_PRINCIPAL_TYPE_ALIASES[raw] ?? null;
}
/**
* The `cp_auth.resource_grants.resource_type` domain: the families a resource grant can name.
*
* Who reads it: cp-auth-api's `listGrantsQuerySchema` and `revokeGrantQuerySchema` (the zod
* enums on the `/v1/permissions/grants` filters), cp-auth-api's `createGrantSchema`, whose
* local list restates these four values, and site-admin-api's `permissions-proxy.schema.ts`,
* which re-exports this list for the `/v1/admin/permissions/grants` proxy.
* `CpResourcePermissionResolver.inferResourceType` keeps its own three-family list
* (`app`, `tenant`, `dp`) and throws `UnresolvableResourcePermissionError` for any other
* segment 2, `platform` included (R-002).
*
* Values, in stored order. The order is appended to, never reordered, because the
* `resource_grants_resource_type_check` CHECK and the Drizzle column enum restate it:
*  - `app`, `tenant`, `dp`: one grant row per app, tenant or data plane, `resource_id` is
*    that entity's id.
*  - `platform` (door-publish-auth, D7 and D12): the control plane itself. There is one
*    platform resource per CP and its `resource_id` is always {@link PLATFORM_RESOURCE_ID}
*    (DF-1). A `platform` grant carries CP-wide authority that no app, tenant or data
*    plane owner holds. cp-auth-api refuses to create one for a caller without
*    `cp:platform:admin` (403), and refuses one whose role holds any string outside
*    `PLATFORM_RESOURCE_PERMISSIONS`, today `cp:platform:publish`,
*    `cp:platform:registry-read` and `cp:app:registry-read` (400; door-publish-auth R-002,
*    door-npm-read-tier R-001, 2026-10-15-per-dp-door-identity D3). The three seeded roles
*    are `platform_publisher`, `platform_registry_reader` and `app_registry_reader`. The
*    admin grant route also refuses a role that holds `cp:app:registry-read` (400), because
*    only the door-pull principal of a data plane holds it, and cp-auth-api writes that
*    grant itself. A custom role that holds any other subset of the platform strings is
*    accepted too.
*
* Adding `platform` here moves the TypeScript domain only. The SQL CHECK, the Drizzle
* column enum and cp-auth-api's own `ResourceType` union each move in their own edit
* and their own migration; a constant edit alone type-checks and then fails the INSERT
* with SQLSTATE 23514.
*
* No route carries a `@RequireResourcePermission` on a `cp:platform:*` string. The
* registry door is the only reader of a `platform` grant, through `/v1/auth/me/grants`.
*/
const GRANT_RESOURCE_TYPES = [
	"app",
	"tenant",
	"dp",
	"platform"
];
/**
* The `resource_id` of the one `platform` resource on every control plane.
*
* What it is for: a `platform` grant has no entity row to point at, because the resource
* is the CP itself, and `cp_auth` has no resource table. Every `resource_grants` row with
* `resource_type = 'platform'` carries this id. Three places hold that rule: the CHECK
* `resource_grants_platform_resource_id_check` (migration 0038), cp-auth-api's
* `assertPlatformGrantShape`, which refuses a `platform` grant naming any other id with a
* 400, and the platform-publisher runbook's `POST /v1/permissions/grants` body. It is a
* constant, never a query parameter or a caller choice (DF-1), so two platform grants on
* one CP always name the same resource.
*
* Who reads it: cp-auth-api (`assertPlatformGrantShape`; the migration and the Drizzle
* CHECK carry the literal, and a migration spec pins the two equal) and the registry
* door. The door is not a workspace package, so `proxy-lib.mjs` holds its own copy of the
* literal, and its two platform legs, `platformPublishLeg` and `platformReadLeg`, admit a
* `/v1/auth/me/grants` row only when its `resourceType` is `platform`, its `permission` is
* the leg's string (`cp:platform:publish` or `cp:platform:registry-read`) and its
* `resourceId` equals this value.
*
* Shape: a fixed RFC 9562 version-4 UUID (version nibble `4`, variant nibble `8`), so any
* `z.uuid()` or Postgres `uuid` cast downstream accepts it, like an app or data plane id.
* The same bytes serve as a fixture id in several test suites. Every authority read in cp-auth-api pairs `resource_id` with
* `resource_type`, so a `tenant` row on that org id and the `platform` row stay distinct.
* The value never changes; a change would orphan every live platform grant.
*/
const PLATFORM_RESOURCE_ID = "00000000-0000-4000-8000-000000000001";
/**
* The RUNTIME pin for the frozen shape (DECISIONS R-23(2)(d)).
*
* ⛔ It exists because "this is the frozen shape" needs something a test can
* assert against, and until now this file declared **interfaces only** — which no
* test can interrogate at run time. It is `.strict()`, so `items`, `nextCursor`
* and `expansionTruncated` are all REJECTED: the freeze is expressed as a
* refusal, not as a comment.
*
* ⚠️ The element schema is `.strict()` too. A permissive element inside a strict
* envelope would let a widened triple through the one check that exists to
* forbid widening, and strict-by-default is this repo's declared posture
* (CLAUDE.md → Validation).
*
* ⛔ **`.readonly()` on the array is REQUIRED by the witness below, not a
* stylistic choice.** The interface declares `readonly grants: readonly Dto[]`,
* and a `readonly T[]` is NOT assignable to a mutable `T[]` — so without this the
* witness's BACKWARD direction is false and it resolves to `never`. Measured: it
* is the only direction that fails, and it fails on correct code, which would
* have made the witness permanently red and therefore worthless as a signal.
* (Property-level `readonly` does not affect assignability; only the array does.)
*/
const resourceGrantSnapshotSchema = z.object({
	grants: z.array(z.object({
		permission: z.string(),
		resourceType: z.string(),
		resourceId: z.string(),
		grantId: z.string(),
		viaGroupId: z.string().nullable()
	}).strict()).readonly(),
	truncated: z.boolean()
}).strict();
//#endregion
export { ACCEPTED_GRANT_PRINCIPAL_TYPES, DEPRECATED_GRANT_PRINCIPAL_TYPE_ALIASES, GRANT_PRINCIPAL_TYPES, GRANT_RESOURCE_TYPES, PLATFORM_RESOURCE_ID, normalizeGrantPrincipalType, resourceGrantSnapshotSchema };

//# sourceMappingURL=grant-principal.js.map